import getConfig from 'next/config'
import { ImageLoaderProps } from 'next/image'
import { Brand } from '@awaze/design-system.components.awaze-theme-provider'
import { SupportedBrand } from '../../lib/properties/properties.types'

export function getAssetUrl(path: string): string {
  const { CDN_PREFIX } = getCurrentPublicRuntimeConfig()
  return CDN_PREFIX ? `${CDN_PREFIX}/public${path}` : path
}

export function getImageAssetUrl({ src, width }: ImageLoaderProps): string {
  const imageOpts = `${src}?w=${width}`

  return getAssetUrl(imageOpts)
}

export function isFeatureEnabled(featureToggle: string): boolean {
  const publicRuntimeConfig = getCurrentPublicRuntimeConfig()
  return !!publicRuntimeConfig?.features?.[featureToggle]
}

export function buildSeoCanonical(
  host: string,
  url: string,
): string | undefined {
  if (host === undefined || url === undefined) {
    return undefined
  }

  // Rewrite all homepage or / to just be the host.
  // Rewrite HOS pages e.g. homepage/lodges to just be the host/lodges
  url =
    url === 'homepage' || url === '/' ? '' : `/${url.replace('homepage/', '')}`

  return `https://${host}${url}`
}

export const getBaseUrl = (brand: Brand = 'cottages.com'): string => {
  if (process.env.NODE_ENV === 'production') {
    return ''
  }
  const publicRuntimeConfig = getCurrentPublicRuntimeConfig()
  return publicRuntimeConfig?.[brand]?.baseUrl
}

export function getEndOfDayAsUTCString(): string {
  return getEndOfDay().toUTCString()
}

export function getEndOfDay(): Date {
  const now = new Date()
  return new Date(
    Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate(),
      23,
      59,
      59,
    ),
  )
}

export function setExpirationInHours(hours: number): Date {
  const now = new Date()
  return new Date(
    Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate(),
      now.getUTCHours() + hours,
      now.getUTCMinutes(),
      now.getUTCSeconds(),
    ),
  )
}

interface ImageSettings {
  w?: string
  h?: string
  q?: string
  fm?: string
  fit?: 'pad' | 'fill' | 'scale' | 'crop' | 'thumb'
}

export const setImageParams = (
  url: string,
  imageSettings: ImageSettings,
): string => {
  if (typeof url === 'string' && url.startsWith('data:')) {
    return url
  }

  const [originpart, searchPart] = url?.split?.('?') || []

  if (!originpart) {
    return ''
  }
  const searchParams = new URLSearchParams(searchPart)

  for (const [param, value] of Object.entries(imageSettings).filter(
    ([, v]) => v !== undefined,
  )) {
    searchParams.set(param, value)
  }

  return `${originpart}?${searchParams}`
}
type BrandConfig = {
  baseUrl: string
  addLocationTagToSearches?: boolean
}

type PublicRuntimeConfig = {
  hoseasons: BrandConfig
  'cottages.com': BrandConfig
  novasol: BrandConfig
  fincallorca: BrandConfig
  jamesVillas: BrandConfig
  LOCATION_API_KEY: string
  PLACES_API_AKAMAI_KEY: string
  PLACES_API_URI: string
  ACQ_PLACES_API_URI: string
  AKAMAI_KEY: string
  CDN_PREFIX: string | null
  features: {
    newsletter: boolean
    placesApiOptimizelyVariableKey: string
  }
  APP_ENV: 'dev' | 'pprd' | 'prd'
  CONTENTFUL_SPACE_ID: string
  tmsId: string
  guestAppTmsId: string
  RETRIEVE_USER_ROUTE: string
}

export const getCurrentPublicRuntimeConfig = (): PublicRuntimeConfig => {
  const {
    publicRuntimeConfig: {
      APP_ENV,
      CONTENTFUL_SPACE_ID,
      CDN_PREFIX,
      RETRIEVE_USER_ROUTE,
      ...runtimeConfig
    },
  } = getConfig()
  runtimeConfig[APP_ENV].APP_ENV = APP_ENV
  runtimeConfig[APP_ENV].CONTENTFUL_SPACE_ID = CONTENTFUL_SPACE_ID
  runtimeConfig[APP_ENV].CDN_PREFIX = CDN_PREFIX
  runtimeConfig[APP_ENV].RETRIEVE_USER_ROUTE = RETRIEVE_USER_ROUTE
  return runtimeConfig[APP_ENV]
}

export const parseSetCookie = (str: string): Record<string, string> =>
  str
    .split(';')
    .map((v) => v.split('='))
    .reduce((acc, v) => {
      const key = !v[1] ? 'true' : decodeURIComponent(v[1].trim())
      acc[decodeURIComponent(v[0].trim())] = key
      return acc
    }, {})

export const isLocaleInLocaleList = (
  locale: string,
  localesToCheck: string[],
): boolean => {
  function extractLocale(str: string): string {
    const MAX_LENGTH = 100
    if (str.length > MAX_LENGTH) {
      throw new Error('Input string is too long')
    }
    const match = RegExp(/\(([^)]+)\)$/).exec(str)
    return match ? match[1] : ''
  }

  const localesNotToInclude = localesToCheck?.map((locale) =>
    extractLocale(locale),
  )

  return localesNotToInclude?.includes(locale)
}

export const brandTagOf = (brand: SupportedBrand): string => {
  switch (brand) {
    case 'cottages.com':
      return 'brandCottages'
    case 'hoseasons':
      return 'brandHoseasons'
    case 'novasol':
      return 'brandNovasol'
    case 'fincallorca':
      return 'brandFincallorca'
    case 'jamesVillas':
      return 'brandJamesVillas'
  }
  throw new Error(`Unsupported brand ${brand}`)
}
